import React, { useCallback, useState } from 'react';
import styled, { css } from 'styled-components';
import {
  ContactFilters__Input,
  SegmentFieldsFragment,
  useGetMySegmentsQuery,
  useInsertSegmentMutation,
} from '~/graphql/types';
import useCurrentAccount from '~/hooks/useCurrentAccount';
import useCurrentUser from '~/hooks/useCurrentUser';
import useAddToast from '~/hooks/useAddToast';
import formatToastMessage from '~/util/formatToastMessage';
import SegmentRow from './components/SegmentRow';
import { useRecoilState } from 'recoil';
import TEST_ID from './index.testid';
import {
  FilterBlockContainer,
  ShowMoreContainer,
  ListContainer,
} from '../../commonComponents';
import { useTransition, animated } from 'react-spring';
import TipBanner from '~/components/organism/TipBanner';
import { ExternalLink } from '~/components/molecule/Link';
import InputWithButtons from '~/components/molecule/InputWithButtons';
import activeContactSegment from './state/activeFilter';
import { isEmpty as isEmptyString } from '~/util/Validation/String';
import useGlobalKeyBinding from '~/hooks/useGlobalKeyBinding';
import { Heading4, Variant } from '~/components/atom/Typography';
import sortByCreatedDate from '~/util/sortByCreatedDate';
import Divider from '~/components/atom/Divider';
import { HELP_DESK_LINK } from '~/constants';
import useTrackEvent from '~/hooks/useTrackEvent';
import hasValue from '~/util/hasValue';

export type Props = {
  currentFilters: ContactFilters__Input;
  onFilterChangeV2: (params: ContactFilters__Input) => void;
};

const text = {
  showMore: 'Toon meer',
  showLess: 'Toon minder',
  inputPlaceholder: 'Naam filter',
  inputLabel: 'Deze filter opslaan',
  title: 'Opgeslagen filters',
  insertErrorMessage: 'Er is iets misgegaan bij het toevoegen van het filter',
  insertSuccessMessage: 'Filter is succesvol toegevoegd',
  tipMessage: 'Sla filters op om deze met één muisklik aan te roepen.',
  moreInfo: 'Meer in ons kenniscentrum',
};

const ITEM_COUNT_TO_DISPLAY = 15;

const Segments: React.FCC<Props> = ({
  dataTestId,
  currentFilters,
  onFilterChangeV2,
}) => {
  const { id: accountId } = useCurrentAccount();
  const { id: userId } = useCurrentUser();
  const addToast = useAddToast();
  const trackEvent = useTrackEvent();
  const [showAllItems, setShowAllItems] = useState<boolean>(false);
  const [filterName, setFilterName] = useState<string>('');

  const [
    { segmentId: activeSegmentId, filterId: activeFilterId },
    setActiveFilter,
  ] = useRecoilState(activeContactSegment);

  const { data: segmentData, updateQuery } = useGetMySegmentsQuery({
    variables: { accountId },
  });

  const [insertSegment, { loading: insertLoading }] =
    useInsertSegmentMutation();

  const hasCurrentFilters = !!currentFilters.nodes[0]?.Node?.nodes.length;
  const hasSelectedFromLeftFilterBar = activeSegmentId || activeFilterId;

  const inputTransition = useTransition(
    !hasSelectedFromLeftFilterBar && hasCurrentFilters,
    {
      from: { opacity: 0, height: '0px' },
      enter: { opacity: 1, height: '70px' },
      leave: { opacity: 0, height: '0px' },
    },
  );

  const onInsert = useCallback(() => {
    if (!filterName) return;

    void insertSegment({
      variables: {
        accountId,
        userId,
        name: filterName,
        filters: {
          ...currentFilters,
          // Filter out empty filter groups for instance: {Node: {connector: "And", negate: false, nodes: [{}]}}
          nodes: currentFilters.nodes.filter(
            node => node.Node?.nodes?.length && hasValue(node.Node.nodes[0]),
          ),
        },
      },
    }).then(({ data, errors }) => {
      if (errors && errors.length > 0) {
        return addToast([
          formatToastMessage(text.insertErrorMessage, 'danger'),
        ]);
      }

      if (data && data.insertSegment) {
        updateQuery(prev => ({
          ...prev,
          getMySegments: [data.insertSegment, ...prev.getMySegments],
        }));

        setActiveFilter({
          segmentId: data.insertSegment.id,
          filterId: null,
        });

        setFilterName('');

        /** ----- TRACKING: contact_filter_saved ----- */
        trackEvent({
          eventName: 'contact_filter_saved',
          parameters: {
            filter_name: filterName,
            filter_criteria: JSON.stringify(currentFilters),
          },
        });

        return addToast([
          formatToastMessage(text.insertSuccessMessage, 'success'),
        ]);
      }
    });
  }, [
    filterName,
    insertSegment,
    accountId,
    userId,
    currentFilters,
    addToast,
    updateQuery,
    setActiveFilter,
    trackEvent,
  ]);

  useGlobalKeyBinding({
    keys: 'enter',
    callback: onInsert,
    enabled: true,
  });

  if (!segmentData || !segmentData.getMySegments) {
    return null;
  }

  const sortedSegments = sortByCreatedDate(
    segmentData.getMySegments,
  ) as Array<SegmentFieldsFragment>;

  const segmentList = showAllItems
    ? sortedSegments
    : sortedSegments.slice(0, ITEM_COUNT_TO_DISPLAY);

  return (
    <FilterBlockContainer data-testid={dataTestId}>
      <Heading4 variant={Variant.primary}>{text.title}</Heading4>
      <Divider margin={['xxxs', null]} />

      <TipBanner
        id="contact-segments-tip"
        headerText={text.title}
        margin={['xxs', 'xs', 'xs', null]}
        dismissButtonPosition="start"
        fontSize="base"
      >
        {text.tipMessage}
        <br />
        <br />
        <ExternalLink
          to={`${HELP_DESK_LINK}/nl/articles/5991139-het-filteren-van-de-contactenlijst`}
          target="_blank"
          rel="noopener noreferrer"
        >
          {text.moreInfo}
        </ExternalLink>
      </TipBanner>

      {inputTransition((style, item: boolean) => (
        <>
          {item && (
            <InputContainer style={style}>
              <InputWithButtons
                value={filterName}
                onChange={e => {
                  setFilterName(e.target.value);
                }}
                buttonActions={[
                  {
                    onClick: onInsert,
                    loading: insertLoading,
                    disabled: isEmptyString(filterName),
                    dataTestId: TEST_ID.INSERT_SEGMENT_BUTTON,
                    icon: 'check',
                    appearance: 'success',
                  },
                ]}
                name="insert-segment-input"
                placeholder={text.inputPlaceholder}
                label={{ text: text.inputLabel }}
                disabled={insertLoading}
              />
            </InputContainer>
          )}
        </>
      ))}

      <ListContainer data-testid={TEST_ID.SEGMENT_LIST}>
        {segmentList.map(({ id, name, filters }) => (
          <SegmentRow
            key={id}
            id={id}
            name={name}
            filters={filters}
            currentFilters={currentFilters}
            onFilterChangeV2={onFilterChangeV2}
            updateQuery={updateQuery}
          />
        ))}
      </ListContainer>

      {segmentData.getMySegments.length > ITEM_COUNT_TO_DISPLAY && (
        <ShowMoreContainer onClick={() => setShowAllItems(!showAllItems)}>
          {showAllItems ? text.showLess : text.showMore}
        </ShowMoreContainer>
      )}
    </FilterBlockContainer>
  );
};

const InputContainer = styled(animated.div)(
  ({ theme }) => css`
    width: 100%;
    margin: 0 ${theme.space('xxs')} 0 0;
  `,
);

export default Segments;
